import { useEffect, useId, useRef, useState } from "react";
import type {
  CommunityActor,
  CommunityComment,
  CommunityPost,
  CommunityPostKind,
  CommunityStartup,
  Product,
} from "@outpost/shared";
import {
  LOOKOUT_MAX_LEVEL,
  lookoutXpProgress,
} from "./questCatalog";

type Props = {
  token: string;
  /** Open a destination (e.g. workspace → My profile). */
  focusDest?: Dest | null;
  /** Bumps when parent re-requests the same focusDest. */
  focusNonce?: number;
  /** Bumps to open My profile with the + New startup form. */
  newStartupNonce?: number;
  onDestChange?: (dest: Dest) => void;
  /** Refresh sidebar product spaces after create. */
  onProductsChanged?: () => void;
  lookoutLevel?: number;
  lookoutXp?: number;
  products?: Product[];
  logoPreviews?: Record<string, string>;
  logoBusyId?: string | null;
  onUploadLogo?: (productId: string, file: File) => void;
};

export type CommunityDest =
  | "feed"
  | "lookouts"
  | "products"
  | "new"
  | "share"
  | "profile";
type Dest = CommunityDest;

type PageEntry =
  | { kind: "founder"; id: string }
  | { kind: "startup"; id: string };

type FounderForm = {
  displayName: string;
  bio: string;
  oneLiner: string;
  hook: string;
  channels: string;
  avatarEmoji: string;
  twitterHandle: string;
  avatarR2Key: string;
  autoShareQuestComplete: boolean;
  alertsEnabled: boolean;
};

type StartupForm = {
  name: string;
  tagline: string;
  about: string;
  url: string;
  stage: string;
  productName: string;
  channels: string;
};

const OUTPOST_SITE = "https://outpost16.com";
const OUTPOST_EMBED = `<a href="${OUTPOST_SITE}">Outpost16</a>`;

const LEVEL_FILTERS = ["all", 1, 2, 3, 4] as const;
const CHANNEL_FILTERS = [
  "all",
  "Buffer",
  "Instantly",
  "PostHog",
  "Stripe",
  "Ahrefs",
  "GA",
  "Loom",
] as const;

const STARTUP_STAGES = [
  "Pre-revenue",
  "Shipping",
  "Freemium",
  "Paid",
] as const;

const POST_KINDS: Array<{
  id: CommunityPostKind;
  label: string;
  placeholder: string;
}> = [
  {
    id: "update",
    label: "Update",
    placeholder: "What did you ship?",
  },
  {
    id: "ask",
    label: "Ask",
    placeholder: "What do you need peers to weigh in on?",
  },
  {
    id: "quest_complete",
    label: "Quest complete",
    placeholder: "Which quest did you finish? (proof welcome)",
  },
];

const INVITE_TEXT =
  "Join me on Outpost16 — a lookout for solo founders shipping together.\nhttps://outpost16.com";

export const COMMUNITY_INVITE_TEXT = INVITE_TEXT;
export const COMMUNITY_INVITE_XP = 40;

export const COMMUNITY_SUBNAV = [
  { id: "feed" as const, label: "Feed" },
  { id: "lookouts" as const, label: "Lookouts" },
  { id: "products" as const, label: "Products" },
];

const COMPOSER_EMOJIS = [
  "🔥",
  "✅",
  "👀",
  "🚀",
  "💬",
  "📸",
  "🎯",
  "🙌",
  "😅",
  "❤️",
  "🛠️",
  "📈",
] as const;

const NEW_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

const glyphForKind = (kind: CommunityPostKind): string => {
  if (kind === "ask") return "/brand/outpost16-quest-assign-flag.png";
  if (kind === "quest_complete") return "/brand/outpost16-quest-type-tool.png";
  return "/brand/outpost16-quest-type-landing.png";
};

const labelForKind = (kind: CommunityPostKind): string => {
  if (kind === "ask") return "Ask";
  if (kind === "quest_complete") return "Quest complete";
  return "Update";
};

const avatarMark = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[parts.length - 1]![0] ?? ""}`.toUpperCase();
  }
  return (parts[0] ?? "?").slice(0, 2).toUpperCase();
};

const trunc = (s: string, n: number): string =>
  s.length <= n ? s : `${s.slice(0, n - 1)}…`;

const parseSqliteDate = (raw: string): number => {
  const t = Date.parse(raw.includes("T") ? raw : `${raw.replace(" ", "T")}Z`);
  return Number.isNaN(t) ? NaN : t;
};

const relativeTime = (iso: string): string => {
  const t = parseSqliteDate(iso);
  if (Number.isNaN(t)) return iso;
  const mins = Math.round((Date.now() - t) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const isNewJoin = (joinedAt: string | null | undefined): boolean => {
  if (!joinedAt) return false;
  const t = parseSqliteDate(joinedAt);
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= NEW_WINDOW_MS;
};

const xpFromMeta = (meta: string | null): string | null => {
  if (!meta) return null;
  const m = meta.match(/\+?\d+\s*XP/i);
  return m ? m[0].replace(/^(\d)/, "+$1").replace(/^\+\+/, "+") : null;
};

const channelsToText = (channels: string[]): string => channels.join(", ");

const textToChannels = (raw: string): string[] =>
  raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const emptyFounderForm = (): FounderForm => ({
  displayName: "",
  bio: "",
  oneLiner: "",
  hook: "",
  channels: "",
  avatarEmoji: "",
  twitterHandle: "",
  avatarR2Key: "",
  autoShareQuestComplete: true,
  alertsEnabled: true,
});

const emptyStartupForm = (): StartupForm => ({
  name: "",
  tagline: "",
  about: "",
  url: "",
  stage: "Shipping",
  productName: "",
  channels: "",
});

async function apiGet<T>(
  path: string,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    return { ok: false, error: (await res.text()).slice(0, 200) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiPost<T>(
  path: string,
  token: string,
  body?: unknown,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const res = await fetch(path, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    return { ok: false, error: (await res.text()).slice(0, 200) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiPatch<T>(
  path: string,
  token: string,
  body: unknown,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const res = await fetch(path, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    return { ok: false, error: (await res.text()).slice(0, 200) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiDelete<T>(
  path: string,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const res = await fetch(path, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    return { ok: false, error: (await res.text()).slice(0, 200) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiPutBlob<T>(
  path: string,
  token: string,
  body: Blob,
  contentType: string,
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const res = await fetch(path, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": contentType,
    },
    body,
  });
  if (!res.ok) {
    return { ok: false, error: (await res.text()).slice(0, 200) };
  }
  return { ok: true, data: (await res.json()) as T };
}

/** Auth-gated R2 image → object URL (avatars + proof). */
const MediaImage = ({
  token,
  imageR2Key,
  alt,
  className,
}: {
  token: string;
  imageR2Key: string;
  alt?: string;
  className?: string;
}) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    void (async () => {
      const res = await fetch(
        `/v1/community/media?key=${encodeURIComponent(imageR2Key)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok || cancelled) return;
      const blob = await res.blob();
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      revoked = url;
      setSrc(url);
    })();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [token, imageR2Key]);

  if (!src) {
    return (
      <span className={className ?? "op-community-proof-skel"} aria-hidden />
    );
  }
  return <img className={className} src={src} alt={alt ?? ""} />;
};

/** Auth-gated product logo → object URL. */
const ProductLogo = ({
  token,
  productId,
  logoR2Key,
  name,
  previewUrl,
  className,
  large,
}: {
  token: string;
  productId: string | null | undefined;
  logoR2Key: string | null | undefined;
  name: string;
  previewUrl?: string | null;
  className?: string;
  large?: boolean;
}) => {
  const [src, setSrc] = useState<string | null>(previewUrl ?? null);

  useEffect(() => {
    if (previewUrl) {
      setSrc(previewUrl);
      return;
    }
    if (!productId || !logoR2Key) {
      setSrc(null);
      return;
    }
    let revoked: string | null = null;
    let cancelled = false;
    void (async () => {
      const res = await fetch(`/v1/products/${encodeURIComponent(productId)}/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || cancelled) return;
      const blob = await res.blob();
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      revoked = url;
      setSrc(url);
    })();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [token, productId, logoR2Key, previewUrl]);

  const boxClass = `op-product-logo${large ? " op-product-logo-lg" : ""}${className ? ` ${className}` : ""}`;

  if (src) {
    return (
      <span className={boxClass} aria-hidden>
        <img src={src} alt="" />
      </span>
    );
  }
  return (
    <span className={`${boxClass} op-product-logo-missing`} aria-hidden>
      {avatarMark(name)}
    </span>
  );
};

/** Auth-gated R2 proof image → object URL. */
const ProofImage = ({
  token,
  imageR2Key,
  alt = "Visual proof",
}: {
  token: string;
  imageR2Key: string;
  alt?: string;
}) => {
  return (
    <div className="op-community-proof">
      <MediaImage
        token={token}
        imageR2Key={imageR2Key}
        alt={alt}
        className="op-community-proof-img"
      />
    </div>
  );
};

const ActorAvatar = ({
  name,
  emoji,
  imageR2Key,
  token,
  size = "md",
}: {
  name: string;
  emoji?: string | null;
  imageR2Key?: string | null;
  token?: string;
  size?: "md" | "lg";
}) => (
  <span
    className={`op-avatar op-avatar-${size}${emoji && !imageR2Key ? " op-community-avatar-emoji" : ""}${imageR2Key ? " op-avatar-photo" : ""}`}
    aria-hidden
  >
    {imageR2Key && token ? (
      <MediaImage
        token={token}
        imageR2Key={imageR2Key}
        alt=""
        className="op-avatar-img"
      />
    ) : (
      emoji?.trim() || avatarMark(name)
    )}
  </span>
);

export const CommunityPane = ({
  token,
  focusDest = null,
  focusNonce = 0,
  newStartupNonce = 0,
  onDestChange,
  onProductsChanged,
  lookoutLevel = 1,
  lookoutXp = 0,
  products = [],
  logoPreviews = {},
  logoBusyId = null,
  onUploadLogo,
}: Props) => {
  const [founders, setFounders] = useState<CommunityActor[]>([]);
  const [startups, setStartups] = useState<CommunityStartup[]>([]);
  const [follows, setFollows] = useState<Set<string>>(new Set());
  const [welcomed, setWelcomed] = useState<Set<string>>(() => new Set());
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [levelFilter, setLevelFilter] =
    useState<(typeof LEVEL_FILTERS)[number]>("all");
  const [channelFilter, setChannelFilter] =
    useState<(typeof CHANNEL_FILTERS)[number]>("all");
  const [refineOpen, setRefineOpen] = useState(false);
  const [feedMode, setFeedMode] = useState<"all" | "following">("all");
  const [pageStack, setPageStack] = useState<PageEntry[]>([]);
  const [pageFounder, setPageFounder] = useState<CommunityActor | null>(null);
  const [pageFounderStartups, setPageFounderStartups] = useState<
    CommunityStartup[]
  >([]);
  const [pageStartup, setPageStartup] = useState<CommunityStartup | null>(null);
  const [pageStartupFounder, setPageStartupFounder] =
    useState<CommunityActor | null>(null);
  const [newStartupLogoFile, setNewStartupLogoFile] = useState<File | null>(
    null,
  );
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [commentsByPost, setCommentsByPost] = useState<
    Record<string, CommunityComment[]>
  >({});
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>(
    {},
  );
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("op_community_demo_banner") === "1";
    } catch {
      return false;
    }
  });
  const [composer, setComposer] = useState("");
  const [postKind, setPostKind] = useState<CommunityPostKind>("update");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [loadError, setLoadError] = useState("");
  const [activeDest, setActiveDest] = useState<Dest>("feed");
  const [showReward, setShowReward] = useState(false);
  const [founderForm, setFounderForm] = useState<FounderForm>(emptyFounderForm);
  const [startupForm, setStartupForm] = useState<StartupForm>(emptyStartupForm);
  const [myStartups, setMyStartups] = useState<CommunityStartup[]>([]);
  const [founderSnapshot, setFounderSnapshot] = useState<CommunityActor | null>(
    null,
  );
  /** null = locked view; founder | new | startup id */
  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const titleId = useId();
  const feedRef = useRef<HTMLDivElement>(null);
  const lookoutsRef = useRef<HTMLDivElement>(null);
  const newRef = useRef<HTMLDivElement>(null);
  const productsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const topPage = pageStack[pageStack.length - 1] ?? null;
  const selfActorId = founders.find((f) => f.isYou)?.id ?? null;

  const flash = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice(""), 2600);
  };

  const refresh = async () => {
    const [dir, fol, feed, su] = await Promise.all([
      apiGet<{ founders: CommunityActor[] }>("/v1/community/directory", token),
      apiGet<{ actorIds: string[] }>("/v1/community/follows", token),
      apiGet<{ posts: CommunityPost[] }>(
        `/v1/community/posts?scope=${feedMode}`,
        token,
      ),
      apiGet<{ startups: CommunityStartup[] }>("/v1/community/startups", token),
    ]);
    if (!dir.ok || !fol.ok || !feed.ok || !su.ok) {
      setLoadError(
        [dir, fol, feed, su]
          .filter((r) => !r.ok)
          .map((r) => ("error" in r ? r.error : ""))
          .join(" · ") || "Community load failed",
      );
      return;
    }
    setLoadError("");
    setFounders(dir.data.founders);
    setFollows(new Set(fol.data.actorIds));
    setPosts(feed.data.posts);
    setStartups(su.data.startups);
  };

  const loadMyProfileForms = async () => {
    const self = founders.find((f) => f.isYou);
    const founderId = self?.id ?? "robin";
    const fr = await apiGet<{
      founder: CommunityActor;
      startups: CommunityStartup[];
    }>(`/v1/community/founders/${encodeURIComponent(founderId)}`, token);
    if (!fr.ok) {
      flash(`Profile load failed: ${fr.error}`);
      return;
    }
    const f = fr.data.founder;
    setFounderSnapshot(f);
    setFounderForm({
      displayName: f.displayName,
      bio: f.bio,
      oneLiner: f.oneLiner,
      hook: f.hook,
      channels: channelsToText(f.channels),
      avatarEmoji: f.avatarEmoji ?? "",
      twitterHandle: f.twitterHandle ? `@${f.twitterHandle}` : "",
      avatarR2Key: f.avatarR2Key ?? "",
      autoShareQuestComplete: f.autoShareQuestComplete !== false,
      alertsEnabled: f.alertsEnabled !== false,
    });
    const mine = (fr.data.startups ?? []).slice().sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    setMyStartups(mine);
    setProfileLoaded(true);
    onProductsChanged?.();
  };

  const beginEditFounder = () => {
    if (!founderSnapshot) return;
    setFounderForm({
      displayName: founderSnapshot.displayName,
      bio: founderSnapshot.bio,
      oneLiner: founderSnapshot.oneLiner,
      hook: founderSnapshot.hook,
      channels: channelsToText(founderSnapshot.channels),
      avatarEmoji: founderSnapshot.avatarEmoji ?? "",
      twitterHandle: founderSnapshot.twitterHandle
        ? `@${founderSnapshot.twitterHandle}`
        : "",
      avatarR2Key: founderSnapshot.avatarR2Key ?? "",
      autoShareQuestComplete:
        founderSnapshot.autoShareQuestComplete !== false,
      alertsEnabled: founderSnapshot.alertsEnabled !== false,
    });
    setEditTarget("founder");
  };

  const beginEditStartup = (s: CommunityStartup) => {
    setStartupForm({
      name: s.name,
      tagline: s.tagline,
      about: s.about,
      url: s.url ?? "",
      stage: STARTUP_STAGES.includes(s.stage as (typeof STARTUP_STAGES)[number])
        ? s.stage
        : "Shipping",
      productName: s.productName,
      channels: channelsToText(s.channels),
    });
    setEditTarget(s.id);
  };

  const beginNewStartup = () => {
    setStartupForm(emptyStartupForm());
    setNewStartupLogoFile(null);
    setEditTarget("new");
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setStartupForm(emptyStartupForm());
    setNewStartupLogoFile(null);
  };

  const openProfileEditStartup = (startupId: string) => {
    goDest("profile");
    const s = myStartups.find((x) => x.id === startupId) ??
      startups.find((x) => x.id === startupId);
    if (s) beginEditStartup(s);
  };

  const startupLogoKey = (s: CommunityStartup): string | null => {
    if (s.logoR2Key) return s.logoR2Key;
    if (s.productId) {
      return products.find((p) => p.id === s.productId)?.logoR2Key ?? null;
    }
    return null;
  };

  const startupHasLogo = (s: CommunityStartup): boolean =>
    Boolean(
      startupLogoKey(s) ||
        (s.productId && logoPreviews[s.productId]),
    );

  const uploadStartupLogo = async (productId: string, file: File) => {
    const res = await apiPutBlob<{ logoR2Key: string }>(
      `/v1/products/${encodeURIComponent(productId)}/logo`,
      token,
      file,
      file.type || "image/png",
    );
    if (!res.ok) {
      flash(`Logo upload failed: ${res.error}`);
      return false;
    }
    onProductsChanged?.();
    await refresh();
    return true;
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, feedMode]);

  useEffect(() => {
    if (activeDest === "profile" && !profileLoaded && founders.length > 0) {
      void loadMyProfileForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDest, founders, profileLoaded]);

  useEffect(() => {
    if (activeDest !== "profile") return;
    const t = window.setTimeout(() => {
      profileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
    return () => window.clearTimeout(t);
  }, [activeDest]);

  useEffect(() => {
    if (!topPage) {
      setPageFounder(null);
      setPageFounderStartups([]);
      setPageStartup(null);
      setPageStartupFounder(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      if (topPage.kind === "founder") {
        const res = await apiGet<{
          founder: CommunityActor;
          startups: CommunityStartup[];
        }>(
          `/v1/community/founders/${encodeURIComponent(topPage.id)}`,
          token,
        );
        if (cancelled) return;
        if (!res.ok) {
          flash(`Profile failed: ${res.error}`);
          return;
        }
        setPageFounder(res.data.founder);
        setPageFounderStartups(res.data.startups);
        setPageStartup(null);
        setPageStartupFounder(null);
      } else {
        const res = await apiGet<{
          startup: CommunityStartup;
          founder: CommunityActor | null;
        }>(
          `/v1/community/startups/${encodeURIComponent(topPage.id)}`,
          token,
        );
        if (cancelled) return;
        if (!res.ok) {
          flash(`Startup failed: ${res.error}`);
          return;
        }
        setPageStartup(res.data.startup);
        setPageStartupFounder(res.data.founder);
        setPageFounder(null);
        setPageFounderStartups([]);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topPage?.kind, topPage?.id, token]);

  useEffect(() => {
    if (pageStack.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setPageStack((stack) => stack.slice(0, -1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageStack.length]);

  const openFounder = (id: string) => {
    setPageStack((stack) => {
      const top = stack[stack.length - 1];
      if (top?.kind === "founder" && top.id === id) return stack;
      return [...stack, { kind: "founder", id }];
    });
  };

  const openStartup = (id: string | null | undefined) => {
    if (!id) {
      flash("No startup linked yet.");
      return;
    }
    setPageStack((stack) => {
      const top = stack[stack.length - 1];
      if (top?.kind === "startup" && top.id === id) return stack;
      return [...stack, { kind: "startup", id }];
    });
  };

  const popPage = () => {
    setPageStack((stack) => stack.slice(0, -1));
  };

  const focusComposer = () => {
    window.requestAnimationFrame(() => {
      window.setTimeout(() => composerRef.current?.focus(), 80);
    });
  };

  const goDest = (dest: Dest) => {
    setPageStack([]);
    if (dest === "share") {
      setActiveDest("feed");
      onDestChange?.("feed");
      focusComposer();
      window.requestAnimationFrame(() => {
        feedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }
    setActiveDest(dest);
    onDestChange?.(dest);
    if (dest === "feed") {
      feedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (dest === "lookouts") {
      lookoutsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (dest === "new") {
      newRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (dest === "products") {
      productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (dest === "profile") {
      profileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (!profileLoaded) void loadMyProfileForms();
    }
  };

  useEffect(() => {
    if (!focusDest) return;
    goDest(focusDest);
    // Parent re-requests via focusNonce (workspace click).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDest, focusNonce]);

  useEffect(() => {
    if (!newStartupNonce) return;
    let cancelled = false;
    void (async () => {
      setPageStack([]);
      setActiveDest("profile");
      onDestChange?.("profile");
      if (!profileLoaded) {
        await loadMyProfileForms();
      }
      if (cancelled) return;
      beginNewStartup();
      window.requestAnimationFrame(() => {
        profileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newStartupNonce]);

  const xpProgress = lookoutXpProgress(lookoutXp);

  const toggleFollow = async (actorId: string) => {
    setBusy(true);
    const following = follows.has(actorId);
    const res = following
      ? await apiDelete<{ following: boolean }>(
          `/v1/community/follows/${encodeURIComponent(actorId)}`,
          token,
        )
      : await apiPost<{ following: boolean }>("/v1/community/follows", token, {
          actorId,
        });
    setBusy(false);
    if (!res.ok) {
      flash(`Follow failed: ${res.error}`);
      return;
    }
    const nextFollowing = !following;
    setFollows((prev) => {
      const next = new Set(prev);
      if (following) next.delete(actorId);
      else next.add(actorId);
      return next;
    });
    if (nextFollowing && follows.size === 0) {
      setFeedMode("following");
      flash("Following — feed narrowed to your circle");
      setActiveDest("feed");
    } else if (feedMode === "following") {
      void refresh();
    }
  };

  const sendWelcome = async (actorId: string) => {
    if (welcomed.has(actorId)) {
      flash("Already welcomed this session");
      return;
    }
    setBusy(true);
    const res = await apiPost<{
      ok: boolean;
      already?: boolean;
      message?: string;
    }>("/v1/community/welcome", token, { actorId });
    setBusy(false);
    if (!res.ok) {
      flash(`Welcome failed: ${res.error}`);
      return;
    }
    setWelcomed((prev) => new Set(prev).add(actorId));
    flash(
      res.data.already
        ? res.data.message ?? "Already welcomed today"
        : "Welcome posted",
    );
    await refresh();
  };

  const loadComments = async (postId: string) => {
    const res = await apiGet<{ comments: CommunityComment[] }>(
      `/v1/community/posts/${encodeURIComponent(postId)}/comments`,
      token,
    );
    if (!res.ok) {
      flash(`Comments failed: ${res.error}`);
      return;
    }
    setCommentsByPost((m) => ({ ...m, [postId]: res.data.comments }));
  };

  const togglePost = async (postId: string) => {
    if (expandedPostId === postId) {
      setExpandedPostId(null);
      return;
    }
    setExpandedPostId(postId);
    if (!commentsByPost[postId]) await loadComments(postId);
  };

  const submitComment = async (postId: string) => {
    const body = (commentDrafts[postId] ?? "").trim();
    if (!body) {
      flash("Write a comment first.");
      return;
    }
    setBusy(true);
    const res = await apiPost<{ comment: CommunityComment }>(
      `/v1/community/posts/${encodeURIComponent(postId)}/comments`,
      token,
      { body },
    );
    setBusy(false);
    if (!res.ok) {
      flash(`Comment failed: ${res.error}`);
      return;
    }
    setCommentDrafts((d) => ({ ...d, [postId]: "" }));
    setCommentsByPost((m) => ({
      ...m,
      [postId]: [...(m[postId] ?? []), res.data.comment],
    }));
    setPosts((ps) =>
      ps.map((p) =>
        p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p,
      ),
    );
    flash("Comment posted");
  };

  const insertEmoji = (emoji: string) => {
    const el = composerRef.current;
    if (!el) {
      setComposer((c) => c + emoji);
      return;
    }
    const start = el.selectionStart ?? composer.length;
    const end = el.selectionEnd ?? composer.length;
    const next = composer.slice(0, start) + emoji + composer.slice(end);
    setComposer(next);
    window.setTimeout(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const onPickImage = (file: File | null) => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    if (!file) {
      setPendingFile(null);
      setPendingPreview(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      flash("Pick a PNG, JPEG, WebP, or GIF.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      flash("Image must be under 3 MB.");
      return;
    }
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
  };

  const submitPost = async () => {
    const body = composer.trim();
    if (!body && !pendingFile) {
      flash("Write something or attach a proof image.");
      return;
    }
    setBusy(true);
    let imageR2Key: string | undefined;
    if (pendingFile) {
      const up = await apiPutBlob<{ imageR2Key: string }>(
        "/v1/community/media",
        token,
        pendingFile,
        pendingFile.type || "image/jpeg",
      );
      if (!up.ok) {
        setBusy(false);
        flash(`Image upload failed: ${up.error}`);
        return;
      }
      imageR2Key = up.data.imageR2Key;
    }
    const res = await apiPost<{ post: CommunityPost }>(
      "/v1/community/posts",
      token,
      { body: body || undefined, postKind, imageR2Key },
    );
    setBusy(false);
    if (!res.ok) {
      flash(`Post failed: ${res.error}`);
      return;
    }
    setComposer("");
    onPickImage(null);
    setEmojiOpen(false);
    if (postKind === "quest_complete") {
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!reduce) {
        setShowReward(true);
        window.setTimeout(() => setShowReward(false), 900);
      }
      flash("Quest complete shared · +40 XP flavor");
    } else {
      flash(postKind === "ask" ? "Ask posted" : "Update posted");
    }
    setActiveDest("feed");
    await refresh();
  };

  const saveFounderProfile = async () => {
    setBusy(true);
    const res = await apiPatch<{ founder: CommunityActor | null }>(
      "/v1/community/me",
      token,
      {
        displayName: founderForm.displayName,
        bio: founderForm.bio,
        oneLiner: founderForm.oneLiner,
        hook: founderForm.hook,
        channels: textToChannels(founderForm.channels),
        avatarEmoji: founderForm.avatarEmoji,
        twitterHandle: founderForm.twitterHandle,
        avatarR2Key: founderForm.avatarR2Key || null,
        autoShareQuestComplete: founderForm.autoShareQuestComplete,
        alertsEnabled: founderForm.alertsEnabled,
      },
    );
    setBusy(false);
    if (!res.ok) {
      flash(`Save founder failed: ${res.error}`);
      return;
    }
    flash("Founder profile saved");
    setEditTarget(null);
    setProfileLoaded(false);
    await refresh();
    await loadMyProfileForms();
  };

  const uploadAvatarPhoto = async (file: File) => {
    setBusy(true);
    const res = await apiPutBlob<{
      avatarR2Key: string;
      founder: CommunityActor | null;
    }>("/v1/community/me/avatar", token, file, file.type || "image/jpeg");
    setBusy(false);
    if (!res.ok) {
      flash(`Avatar upload failed: ${res.error}`);
      return;
    }
    setFounderForm((f) => ({ ...f, avatarR2Key: res.data.avatarR2Key }));
    if (res.data.founder) setFounderSnapshot(res.data.founder);
    flash("Profile photo updated");
    setProfileLoaded(false);
    await refresh();
    await loadMyProfileForms();
  };

  const verifyStartupBacklink = async (startupId: string) => {
    setBusy(true);
    const res = await apiPost<{
      ok: boolean;
      status: string;
      note: string;
      startup: CommunityStartup | null;
    }>(
      `/v1/community/me/startups/${encodeURIComponent(startupId)}/verify-backlink`,
      token,
    );
    setBusy(false);
    if (!res.ok) {
      flash(`Backlink check failed: ${res.error}`);
      return;
    }
    flash(res.data.note || `Backlink ${res.data.status}`);
    setProfileLoaded(false);
    await refresh();
    await loadMyProfileForms();
  };

  const saveStartupProfile = async () => {
    if (!editTarget || editTarget === "founder") return;

    if (editTarget === "new") {
      if (!newStartupLogoFile) {
        flash("Product logo is required for the product directory.");
        return;
      }
    } else {
      const existing = myStartups.find((s) => s.id === editTarget);
      if (existing?.productId && !startupHasLogo(existing)) {
        flash("Upload a product logo before saving — required for the directory.");
        return;
      }
    }

    setBusy(true);
    const payload = {
      name: startupForm.name,
      tagline: startupForm.tagline,
      about: startupForm.about,
      url: startupForm.url.trim() || null,
      stage: startupForm.stage,
      productName: startupForm.productName || startupForm.name,
      channels: textToChannels(startupForm.channels),
    };

    if (editTarget === "new") {
      const res = await apiPost<{
        startup: CommunityStartup | null;
        product: Product | null;
      }>("/v1/community/me/startups", token, payload);
      if (!res.ok) {
        setBusy(false);
        flash(`Create startup failed: ${res.error}`);
        return;
      }
      if (res.data.product && newStartupLogoFile) {
        const ok = await uploadStartupLogo(
          res.data.product.id,
          newStartupLogoFile,
        );
        if (!ok) {
          setBusy(false);
          return;
        }
      }
      setBusy(false);
      flash(
        res.data.product
          ? `Startup created · ${res.data.product.name} added to product spaces`
          : "Startup created",
      );
      setEditTarget(null);
      setNewStartupLogoFile(null);
      setProfileLoaded(false);
      onProductsChanged?.();
      await refresh();
      await loadMyProfileForms();
      return;
    }

    const res = await apiPatch<{ startup: CommunityStartup | null }>(
      `/v1/community/me/startups/${encodeURIComponent(editTarget)}`,
      token,
      payload,
    );
    setBusy(false);
    if (!res.ok) {
      flash(`Save startup failed: ${res.error}`);
      return;
    }
    flash("Startup profile saved");
    setEditTarget(null);
    setProfileLoaded(false);
    onProductsChanged?.();
    await refresh();
    await loadMyProfileForms();
  };

  const directory = founders.filter((f) => {
    if (levelFilter !== "all" && f.lookoutLevel !== levelFilter) return false;
    if (channelFilter !== "all" && !f.channels.includes(channelFilter))
      return false;
    return true;
  });

  const newLookouts = founders.filter((f) => isNewJoin(f.joinedAt));
  const newStartups = startups.filter((s) => isNewJoin(s.joinedAt));
  const productDirectory = startups
    .slice()
    .sort((a, b) => {
      const aLogo = startupHasLogo(a);
      const bLogo = startupHasLogo(b);
      if (aLogo !== bLogo) return aLogo ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

  const profilePosts = pageFounder
    ? posts.filter((p) => p.actorId === pageFounder.id)
    : [];

  const kindPlaceholder =
    POST_KINDS.find((k) => k.id === postKind)?.placeholder ??
    "Share a lookout update";

  const renderFollowWelcome = (actor: CommunityActor) => {
    if (actor.isYou) return null;
    const isFollowing = follows.has(actor.id);
    const isWelcomed = welcomed.has(actor.id);
    return (
      <div className="op-community-action-row">
        <button
          type="button"
          className={isFollowing ? "op-btn" : "op-primary"}
          disabled={busy}
          aria-pressed={isFollowing}
          onClick={() => void toggleFollow(actor.id)}
        >
          {isFollowing ? "Following" : "Follow"}
        </button>
        <button
          type="button"
          className={isWelcomed ? "op-btn" : "op-chip"}
          disabled={busy || isWelcomed}
          onClick={() => void sendWelcome(actor.id)}
        >
          {isWelcomed ? "Welcomed" : "Welcome"}
        </button>
      </div>
    );
  };

  return (
    <section className="op-pane op-community-pane">
      <div className="op-community-stage">
        <div className="op-community-embers" aria-hidden />
        <div className="op-community-stage-head">
          <div>
            <h1>
              {activeDest === "lookouts"
                ? "Lookouts"
                : activeDest === "products"
                  ? "Products"
                  : activeDest === "new"
                    ? "New joins"
                    : activeDest === "profile"
                      ? "My profile"
                      : "Feed"}
            </h1>
            <small>
              {activeDest === "lookouts"
                ? `${directory.length} lookouts · ${follows.size} following`
                : activeDest === "products"
                  ? `${productDirectory.filter((s) => startupHasLogo(s) || !s.productId).length} listed · peer directory`
                  : activeDest === "profile"
                    ? "Your founder profile and startups"
                    : "Peer-witnessed shipping — proof, follow, ask"}
            </small>
          </div>
        </div>
        {!demoBannerDismissed ? (
          <p className="op-community-stage-note">
            Sample lookouts for atmosphere — follows and posts persist.{" "}
            <button
              type="button"
              className="op-linkish"
              onClick={() => {
                setDemoBannerDismissed(true);
                try {
                  sessionStorage.setItem("op_community_demo_banner", "1");
                } catch {
                  /* ignore */
                }
              }}
            >
              Got it
            </button>
          </p>
        ) : null}
      </div>

      {notice ? (
        <p className="op-notice" aria-live="polite">
          {notice}
        </p>
      ) : null}
      {loadError ? (
        <p className="op-notice err" aria-live="assertive">
          {loadError}{" "}
          <button type="button" className="op-btn" onClick={() => void refresh()}>
            Retry
          </button>
        </p>
      ) : null}

      {showReward ? (
        <div className="op-community-reward" aria-hidden>
          <img src="/brand/outpost16-quest-reward-8frame.png" alt="" />
        </div>
      ) : null}

      {topPage ? (
        <div className="op-community-page">
          <div className="op-community-page-nav">
            <button type="button" className="op-btn" onClick={popPage}>
              ← Back
            </button>
          </div>
          <div className="op-panel-head">
            <div className="op-community-profile-head">
              {topPage.kind === "founder" && pageFounder ? (
                <>
                  <ActorAvatar
                    name={pageFounder.displayName}
                    emoji={pageFounder.avatarEmoji}
                    imageR2Key={pageFounder.avatarR2Key}
                    token={token}
                    size="lg"
                  />
                  <div>
                    <h2 id={titleId}>{pageFounder.displayName}</h2>
                    <small>
                      L{pageFounder.lookoutLevel}
                      {pageFounder.isYou ? " · That’s you" : ""}
                      {pageFounder.kind === "demo" ? " · Demo" : ""}
                    </small>
                    {pageFounder.twitterHandle ? (
                      <p>
                        <a
                          href={`https://x.com/${pageFounder.twitterHandle}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          @{pageFounder.twitterHandle}
                        </a>
                      </p>
                    ) : null}
                  </div>
                </>
              ) : topPage.kind === "startup" && pageStartup ? (
                <>
                  <ProductLogo
                    token={token}
                    productId={pageStartup.productId}
                    logoR2Key={startupLogoKey(pageStartup)}
                    name={pageStartup.name}
                    previewUrl={
                      pageStartup.productId
                        ? logoPreviews[pageStartup.productId]
                        : null
                    }
                    large
                    className="op-community-page-product-logo"
                  />
                  <div>
                    <h2 id={titleId}>{pageStartup.name}</h2>
                    <small>{pageStartup.stage}</small>
                  </div>
                </>
              ) : (
                <div>
                  <h2 id={titleId}>Loading…</h2>
                </div>
              )}
            </div>
          </div>

          {topPage.kind === "founder" && pageFounder ? (
            <>
              {pageFounder.oneLiner ? (
                <p className="op-community-oneliner">{pageFounder.oneLiner}</p>
              ) : null}
              {pageFounder.bio ? (
                <p className="op-community-bio">{pageFounder.bio}</p>
              ) : null}
              <div className="op-chip-row" style={{ marginBottom: 12 }}>
                {pageFounder.channels.map((ch) => (
                  <span key={ch} className="op-tag">
                    {ch}
                  </span>
                ))}
              </div>
              {pageFounder.hook ? (
                <p className="op-community-hook">{pageFounder.hook}</p>
              ) : null}
              <p className="op-community-meta">
                Product · {pageFounder.productName || "—"}
                <br />
                <span className="op-muted-line">~{pageFounder.xpFlavor} XP</span>
              </p>

              {renderFollowWelcome(pageFounder)}

              <div className="op-section-title">
                <h3>Startups</h3>
              </div>
              {pageFounderStartups.length === 0 ? (
                <p className="op-muted-line">No linked startups.</p>
              ) : (
                <div className="op-community-gallery op-community-gallery-compact">
                  {pageFounderStartups.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="op-community-product-card"
                      onClick={() => openStartup(s.id)}
                    >
                      <ProductLogo
                        token={token}
                        productId={s.productId}
                        logoR2Key={startupLogoKey(s)}
                        name={s.name}
                        previewUrl={
                          s.productId ? logoPreviews[s.productId] : null
                        }
                        large
                      />
                      <strong>{s.name}</strong>
                      <small>{trunc(s.tagline || s.stage, 48)}</small>
                    </button>
                  ))}
                </div>
              )}

              <div className="op-section-title">
                <h3>Recent proof</h3>
              </div>
              <div className="op-list op-feed">
                {profilePosts.length === 0 ? (
                  <div className="op-list-item">
                    <div className="op-item-copy">
                      <small>No posts in the current feed scope.</small>
                    </div>
                  </div>
                ) : (
                  profilePosts.map((a) => (
                    <div key={a.id} className="op-list-item">
                      <img
                        className="op-community-type-glyph"
                        src={glyphForKind(a.postKind)}
                        alt=""
                        width={28}
                        height={28}
                      />
                      <div className="op-item-copy">
                        <h3>
                          {a.body === "(visual proof)"
                            ? "Visual proof"
                            : a.body}
                        </h3>
                        <small>
                          {labelForKind(a.postKind)} · {relativeTime(a.createdAt)}
                          {a.imageR2Key ? " · image" : ""}
                        </small>
                        {a.imageR2Key ? (
                          <ProofImage
                            token={token}
                            imageR2Key={a.imageR2Key}
                            alt={`Proof from ${pageFounder.displayName}`}
                          />
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : null}

          {topPage.kind === "startup" && pageStartup ? (
            <>
              {pageStartup.tagline ? (
                <p className="op-community-oneliner">{pageStartup.tagline}</p>
              ) : null}
              {pageStartup.about ? (
                <p className="op-community-bio">{pageStartup.about}</p>
              ) : null}
              <p className="op-community-meta">
                Stage · {pageStartup.stage}
                <br />
                Product · {pageStartup.productName || "—"}
                {pageStartup.url ? (
                  <>
                    <br />
                    Website ·{" "}
                    <a
                      href={pageStartup.url}
                      target="_blank"
                      rel={
                        pageStartup.backlinkStatus === "verified"
                          ? "noopener noreferrer"
                          : "noopener noreferrer nofollow"
                      }
                    >
                      {pageStartup.url}
                    </a>
                    {pageStartup.backlinkStatus === "verified" ? (
                      <span className="op-tag op-tag-verified">
                        {" "}
                        Backlink verified
                      </span>
                    ) : (
                      <span className="op-tag"> Pending reciprocity</span>
                    )}
                  </>
                ) : null}
              </p>
              <div className="op-chip-row" style={{ marginBottom: 12 }}>
                {pageStartup.channels.map((ch) => (
                  <span key={ch} className="op-tag">
                    {ch}
                  </span>
                ))}
              </div>

              <p className="op-community-founded">
                Founded by{" "}
                <button
                  type="button"
                  className="op-linkish"
                  onClick={() =>
                    openFounder(
                      pageStartupFounder?.id ?? pageStartup.founderActorId,
                    )
                  }
                >
                  {pageStartupFounder?.displayName ||
                    pageStartup.founderDisplayName ||
                    "founder"}
                </button>
              </p>

              {pageStartupFounder && !pageStartupFounder.isYou
                ? renderFollowWelcome(pageStartupFounder)
                : pageStartup.founderActorId &&
                    !founders.find((f) => f.id === pageStartup.founderActorId)
                      ?.isYou ? (
                    <div className="op-community-action-row">
                      <button
                        type="button"
                        className={
                          follows.has(pageStartup.founderActorId)
                            ? "op-btn"
                            : "op-primary"
                        }
                        disabled={busy}
                        onClick={() =>
                          void toggleFollow(pageStartup.founderActorId)
                        }
                      >
                        {follows.has(pageStartup.founderActorId)
                          ? "Following"
                          : "Follow founder"}
                      </button>
                      <button
                        type="button"
                        className={
                          welcomed.has(pageStartup.founderActorId)
                            ? "op-btn"
                            : "op-chip"
                        }
                        disabled={
                          busy || welcomed.has(pageStartup.founderActorId)
                        }
                        onClick={() =>
                          void sendWelcome(pageStartup.founderActorId)
                        }
                      >
                        {welcomed.has(pageStartup.founderActorId)
                          ? "Welcomed"
                          : "Welcome founder"}
                      </button>
                    </div>
                  ) : null}
            </>
          ) : null}
        </div>
      ) : activeDest === "profile" ? (
        <div
          ref={profileRef}
          id="op-community-profile"
          className="op-community-my-profile"
        >
          <div className="op-section-title" style={{ marginTop: 0 }}>
            <div className="op-section-title-lead">
              <h2>My profile</h2>
            </div>
            <span className="op-tag">L{lookoutLevel}</span>
          </div>

          <div
            className="op-lookout-progress"
            role="status"
            aria-label={`Lookout level ${xpProgress.level}, ${lookoutXp} XP`}
          >
            <div className="op-lookout-progress-meta">
              <strong>Lookout L{xpProgress.level}</strong>
              <span>
                {xpProgress.nextLevel
                  ? `${xpProgress.intoLevel} / ${xpProgress.span} XP → L${xpProgress.nextLevel}`
                  : `Max L${LOOKOUT_MAX_LEVEL}`}
              </span>
            </div>
            <div
              className="op-lookout-progress-track"
              aria-hidden
            >
              <div
                className="op-lookout-progress-fill"
                style={{ width: `${xpProgress.pct}%` }}
              />
            </div>
            <p className="op-lookout-progress-hint">
              {xpProgress.nextLevel
                ? `${xpProgress.xpToNext} XP to next lookout level · confirmed quests only`
                : "You’ve reached the top of the lookout ladder."}
            </p>
          </div>

          <div className="op-profile-stack">
            <section className="op-profile-card">
              <div className="op-profile-card-head">
                <h3>Founder</h3>
                {editTarget === "founder" ? (
                  <button
                    type="button"
                    className="op-btn"
                    disabled={busy}
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    className="op-btn"
                    disabled={busy || !founderSnapshot}
                    onClick={beginEditFounder}
                  >
                    Edit
                  </button>
                )}
              </div>

              {editTarget === "founder" ? (
                <form
                  className="op-community-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveFounderProfile();
                  }}
                >
                  <label className="op-community-field">
                    <span>Display name</span>
                    <input
                      className="op-field"
                      value={founderForm.displayName}
                      onChange={(e) =>
                        setFounderForm((f) => ({
                          ...f,
                          displayName: e.target.value,
                        }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>Bio</span>
                    <textarea
                      className="op-field op-community-textarea"
                      rows={3}
                      value={founderForm.bio}
                      onChange={(e) =>
                        setFounderForm((f) => ({ ...f, bio: e.target.value }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>One-liner</span>
                    <input
                      className="op-field"
                      value={founderForm.oneLiner}
                      onChange={(e) =>
                        setFounderForm((f) => ({
                          ...f,
                          oneLiner: e.target.value,
                        }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>Hook</span>
                    <textarea
                      className="op-field op-community-textarea"
                      rows={2}
                      value={founderForm.hook}
                      onChange={(e) =>
                        setFounderForm((f) => ({ ...f, hook: e.target.value }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>Channels (comma-separated)</span>
                    <input
                      className="op-field"
                      value={founderForm.channels}
                      onChange={(e) =>
                        setFounderForm((f) => ({
                          ...f,
                          channels: e.target.value,
                        }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>X / Twitter</span>
                    <input
                      className="op-field"
                      value={founderForm.twitterHandle}
                      onChange={(e) =>
                        setFounderForm((f) => ({
                          ...f,
                          twitterHandle: e.target.value,
                        }))
                      }
                      disabled={busy}
                      placeholder="@handle or x.com/handle"
                    />
                  </label>
                  <div className="op-community-field">
                    <span>Profile photo</span>
                    <div className="op-profile-avatar-edit">
                      <ActorAvatar
                        name={founderForm.displayName || "You"}
                        emoji={founderForm.avatarEmoji}
                        imageR2Key={founderForm.avatarR2Key || null}
                        token={token}
                        size="lg"
                      />
                      <label className="op-profile-logo-upload">
                        <span>{busy ? "Uploading…" : "Upload photo"}</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          disabled={busy}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (file) void uploadAvatarPhoto(file);
                          }}
                        />
                      </label>
                    </div>
                  </div>
                  <label className="op-community-field">
                    <span>Fallback emoji (if no photo)</span>
                    <input
                      className="op-field"
                      value={founderForm.avatarEmoji}
                      onChange={(e) =>
                        setFounderForm((f) => ({
                          ...f,
                          avatarEmoji: e.target.value,
                        }))
                      }
                      disabled={busy}
                      maxLength={8}
                    />
                  </label>
                  <div className="op-profile-card-actions">
                    <button type="submit" className="op-primary" disabled={busy}>
                      Save founder
                    </button>
                  </div>
                </form>
              ) : (
                <div className="op-profile-locked-wrap">
                  <div className="op-profile-locked-hero">
                    <ActorAvatar
                      name={founderSnapshot?.displayName || "You"}
                      emoji={founderSnapshot?.avatarEmoji}
                      imageR2Key={founderSnapshot?.avatarR2Key}
                      token={token}
                      size="lg"
                    />
                    <div>
                      <strong>{founderSnapshot?.displayName || "—"}</strong>
                      {founderSnapshot?.twitterHandle ? (
                        <p>
                          <a
                            href={`https://x.com/${founderSnapshot.twitterHandle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            @{founderSnapshot.twitterHandle}
                          </a>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <dl className="op-profile-locked">
                    <div>
                      <dt>One-liner</dt>
                      <dd>{founderSnapshot?.oneLiner || "—"}</dd>
                    </div>
                    <div>
                      <dt>Bio</dt>
                      <dd>{founderSnapshot?.bio || "—"}</dd>
                    </div>
                    <div>
                      <dt>Hook</dt>
                      <dd>{founderSnapshot?.hook || "—"}</dd>
                    </div>
                    <div>
                      <dt>Channels</dt>
                      <dd>
                        {founderSnapshot?.channels?.length
                          ? founderSnapshot.channels.join(" · ")
                          : "—"}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </section>

            <section className="op-profile-card">
              <div className="op-profile-card-head">
                <h3>Sharing</h3>
              </div>
              <label className="op-profile-toggle">
                <input
                  type="checkbox"
                  checked={
                    (editTarget === "founder"
                      ? founderForm.autoShareQuestComplete
                      : founderSnapshot?.autoShareQuestComplete !== false) ??
                    true
                  }
                  disabled={busy || !founderSnapshot}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setFounderForm((f) => ({
                      ...f,
                      autoShareQuestComplete: next,
                    }));
                    setFounderSnapshot((s) =>
                      s ? { ...s, autoShareQuestComplete: next } : s,
                    );
                    void (async () => {
                      setBusy(true);
                      const res = await apiPatch<{
                        founder: CommunityActor | null;
                      }>("/v1/community/me", token, {
                        autoShareQuestComplete: next,
                      });
                      setBusy(false);
                      if (!res.ok) {
                        flash(`Couldn’t update sharing: ${res.error}`);
                        setFounderForm((f) => ({
                          ...f,
                          autoShareQuestComplete: !next,
                        }));
                        setFounderSnapshot((s) =>
                          s
                            ? { ...s, autoShareQuestComplete: !next }
                            : s,
                        );
                        return;
                      }
                      if (res.data.founder) {
                        setFounderSnapshot(res.data.founder);
                      }
                      flash(
                        next
                          ? "Quest completes will post to the feed"
                          : "Auto-share turned off",
                      );
                    })();
                  }}
                />
                <span>
                  <strong>Auto-share quest completes</strong>
                  <small>
                    When you confirm a quest, post it to the community feed.
                    Turn off if you want to keep completions private.
                  </small>
                </span>
              </label>
              <label className="op-profile-toggle">
                <input
                  type="checkbox"
                  checked={
                    (editTarget === "founder"
                      ? founderForm.alertsEnabled
                      : founderSnapshot?.alertsEnabled !== false) ?? true
                  }
                  disabled={busy || !founderSnapshot}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setFounderForm((f) => ({
                      ...f,
                      alertsEnabled: next,
                    }));
                    setFounderSnapshot((s) =>
                      s ? { ...s, alertsEnabled: next } : s,
                    );
                    void (async () => {
                      setBusy(true);
                      const res = await apiPatch<{
                        founder: CommunityActor | null;
                      }>("/v1/community/me", token, {
                        alertsEnabled: next,
                      });
                      setBusy(false);
                      if (!res.ok) {
                        flash(`Couldn’t update alerts: ${res.error}`);
                        setFounderForm((f) => ({
                          ...f,
                          alertsEnabled: !next,
                        }));
                        setFounderSnapshot((s) =>
                          s ? { ...s, alertsEnabled: !next } : s,
                        );
                        return;
                      }
                      if (res.data.founder) {
                        setFounderSnapshot(res.data.founder);
                      }
                      flash(
                        next
                          ? "Activity alerts on"
                          : "Toasts off — Events inbox still records",
                      );
                    })();
                  }}
                />
                <span>
                  <strong>Show activity alerts</strong>
                  <small>
                    Toast popups for follows, agents, quests, and connectors.
                    Events history still records when this is off.
                  </small>
                </span>
              </label>
              <p className="op-profile-hint">
                More event types and feed shares come next.
              </p>
            </section>

            <section className="op-profile-card">
              <div className="op-profile-card-head">
                <h3>Startups &amp; product spaces</h3>
                {editTarget !== "new" ? (
                  <button
                    type="button"
                    className="op-btn"
                    disabled={busy || editTarget === "founder"}
                    onClick={beginNewStartup}
                  >
                    + New
                  </button>
                ) : (
                  <button
                    type="button"
                    className="op-btn"
                    disabled={busy}
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                )}
              </div>
              <p className="op-profile-products-lead">
                One entry per product space ({myStartups.length}
                {products.length ? ` · ${products.length} in workspace` : ""}).
                Logo and website URL are required for the public product
                directory. For a dofollow listing, add Outpost in your footer or
                Partners / Featured on, then verify the reciprocal link.
              </p>

              {editTarget === "new" ? (
                <form
                  className="op-community-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveStartupProfile();
                  }}
                >
                  <h4 className="op-profile-form-title">New startup</h4>
                  <label className="op-community-field">
                    <span>Name</span>
                    <input
                      className="op-field"
                      value={startupForm.name}
                      onChange={(e) =>
                        setStartupForm((s) => ({ ...s, name: e.target.value }))
                      }
                      disabled={busy}
                      required
                      placeholder="Product or company name"
                    />
                  </label>
                  <label className="op-community-field">
                    <span>Tagline</span>
                    <input
                      className="op-field"
                      value={startupForm.tagline}
                      onChange={(e) =>
                        setStartupForm((s) => ({
                          ...s,
                          tagline: e.target.value,
                        }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>About</span>
                    <textarea
                      className="op-field op-community-textarea"
                      rows={3}
                      value={startupForm.about}
                      onChange={(e) =>
                        setStartupForm((s) => ({ ...s, about: e.target.value }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>Website URL (for directory backlink)</span>
                    <input
                      className="op-field"
                      value={startupForm.url}
                      onChange={(e) =>
                        setStartupForm((s) => ({ ...s, url: e.target.value }))
                      }
                      disabled={busy}
                      placeholder="https://"
                      required
                    />
                  </label>
                  <p className="op-profile-products-lead">
                    After create, embed {OUTPOST_EMBED} in your footer or
                    Partners section, then Verify backlink.
                  </p>
                  <label className="op-community-field">
                    <span>Stage</span>
                    <select
                      className="op-field"
                      value={startupForm.stage}
                      onChange={(e) =>
                        setStartupForm((s) => ({
                          ...s,
                          stage: e.target.value,
                        }))
                      }
                      disabled={busy}
                    >
                      {STARTUP_STAGES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="op-community-field">
                    <span>Channels (comma-separated)</span>
                    <input
                      className="op-field"
                      value={startupForm.channels}
                      onChange={(e) =>
                        setStartupForm((s) => ({
                          ...s,
                          channels: e.target.value,
                        }))
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="op-community-field">
                    <span>Product logo (required)</span>
                    <input
                      className="op-field"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                      required
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        setNewStartupLogoFile(file);
                      }}
                    />
                    <small className="op-muted-line">
                      Shown in the product directory gallery.
                    </small>
                  </label>
                  <div className="op-profile-card-actions">
                    <button
                      type="submit"
                      className="op-primary"
                      disabled={
                        busy ||
                        !startupForm.name.trim() ||
                        !newStartupLogoFile
                      }
                    >
                      Create startup + product space
                    </button>
                  </div>
                </form>
              ) : null}

              <ul className="op-profile-startup-list">
                {myStartups.map((s) => {
                  const editing = editTarget === s.id;
                  const linked = s.productId
                    ? products.find((p) => p.id === s.productId)
                    : null;
                  return (
                    <li key={s.id} className="op-profile-startup-item">
                      <div className="op-profile-card-head">
                        <div className="op-profile-startup-title">
                          <strong>{s.name}</strong>
                          <span className="op-tag">{s.stage}</span>
                          {linked || s.productId ? (
                            <span className="op-tag">Product space</span>
                          ) : null}
                        </div>
                        {editing ? (
                          <button
                            type="button"
                            className="op-btn"
                            disabled={busy}
                            onClick={cancelEdit}
                          >
                            Cancel
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="op-btn"
                            disabled={
                              busy ||
                              editTarget === "founder" ||
                              editTarget === "new"
                            }
                            onClick={() => beginEditStartup(s)}
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      {editing ? (
                        <form
                          className="op-community-form"
                          onSubmit={(e) => {
                            e.preventDefault();
                            void saveStartupProfile();
                          }}
                        >
                          <label className="op-community-field">
                            <span>Name</span>
                            <input
                              className="op-field"
                              value={startupForm.name}
                              onChange={(e) =>
                                setStartupForm((prev) => ({
                                  ...prev,
                                  name: e.target.value,
                                }))
                              }
                              disabled={busy}
                              required
                            />
                          </label>
                          <label className="op-community-field">
                            <span>Tagline</span>
                            <input
                              className="op-field"
                              value={startupForm.tagline}
                              onChange={(e) =>
                                setStartupForm((prev) => ({
                                  ...prev,
                                  tagline: e.target.value,
                                }))
                              }
                              disabled={busy}
                            />
                          </label>
                          <label className="op-community-field">
                            <span>About</span>
                            <textarea
                              className="op-field op-community-textarea"
                              rows={3}
                              value={startupForm.about}
                              onChange={(e) =>
                                setStartupForm((prev) => ({
                                  ...prev,
                                  about: e.target.value,
                                }))
                              }
                              disabled={busy}
                            />
                          </label>
                          <label className="op-community-field">
                            <span>Website URL</span>
                            <input
                              className="op-field"
                              value={startupForm.url}
                              onChange={(e) =>
                                setStartupForm((prev) => ({
                                  ...prev,
                                  url: e.target.value,
                                }))
                              }
                              disabled={busy}
                              placeholder="https://"
                            />
                          </label>
                          <div className="op-backlink-box">
                            <strong>Reciprocal backlink</strong>
                            <p>
                              For a dofollow listing, embed{" "}
                              <code>{OUTPOST_EMBED}</code> in your footer or
                              Partners / Featured on section, then verify.
                            </p>
                            <button
                              type="button"
                              className="op-btn"
                              disabled={busy || !startupForm.url.trim()}
                              onClick={() => void verifyStartupBacklink(s.id)}
                            >
                              Verify backlink
                            </button>
                          </div>
                          <label className="op-community-field">
                            <span>Stage</span>
                            <select
                              className="op-field"
                              value={startupForm.stage}
                              onChange={(e) =>
                                setStartupForm((prev) => ({
                                  ...prev,
                                  stage: e.target.value,
                                }))
                              }
                              disabled={busy}
                            >
                              {STARTUP_STAGES.map((st) => (
                                <option key={st} value={st}>
                                  {st}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="op-community-field">
                            <span>Product name</span>
                            <input
                              className="op-field"
                              value={startupForm.productName}
                              onChange={(e) =>
                                setStartupForm((prev) => ({
                                  ...prev,
                                  productName: e.target.value,
                                }))
                              }
                              disabled={busy}
                            />
                          </label>
                          <label className="op-community-field">
                            <span>Channels (comma-separated)</span>
                            <input
                              className="op-field"
                              value={startupForm.channels}
                              onChange={(e) =>
                                setStartupForm((prev) => ({
                                  ...prev,
                                  channels: e.target.value,
                                }))
                              }
                              disabled={busy}
                            />
                          </label>
                          {s.productId && onUploadLogo ? (
                            <label className="op-community-field">
                              <span>Product logo (required for directory)</span>
                              <div className="op-profile-logo-row op-profile-logo-inline">
                                <span
                                  className="op-product-logo"
                                  style={{ width: 40, height: 40 }}
                                  aria-hidden
                                >
                                  {logoPreviews[s.productId] ? (
                                    <img
                                      src={logoPreviews[s.productId]}
                                      alt=""
                                    />
                                  ) : (
                                    s.name.slice(0, 2).toUpperCase()
                                  )}
                                </span>
                                <label className="op-profile-logo-upload">
                                  <span>
                                    {logoBusyId === s.productId
                                      ? "Uploading…"
                                      : logoPreviews[s.productId]
                                        ? "Replace logo"
                                        : "Upload logo"}
                                  </span>
                                  <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                    disabled={logoBusyId === s.productId}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      e.target.value = "";
                                      if (file && s.productId)
                                        onUploadLogo(s.productId, file);
                                    }}
                                  />
                                </label>
                              </div>
                            </label>
                          ) : null}
                          <div className="op-profile-card-actions">
                            <button
                              type="submit"
                              className="op-primary"
                              disabled={busy}
                            >
                              Save startup
                            </button>
                          </div>
                        </form>
                      ) : (
                        <dl className="op-profile-locked">
                          <div>
                            <dt>Tagline</dt>
                            <dd>{s.tagline || "—"}</dd>
                          </div>
                          <div>
                            <dt>About</dt>
                            <dd>{s.about || "—"}</dd>
                          </div>
                          <div>
                            <dt>Website</dt>
                            <dd>
                              {s.url ? (
                                <a
                                  href={s.url}
                                  target="_blank"
                                  rel={
                                    s.backlinkStatus === "verified"
                                      ? "noopener noreferrer"
                                      : "noopener noreferrer nofollow"
                                  }
                                >
                                  {s.url}
                                </a>
                              ) : (
                                "—"
                              )}
                            </dd>
                          </div>
                          <div>
                            <dt>Backlink</dt>
                            <dd>
                              {s.backlinkStatus === "verified"
                                ? "Verified · dofollow listing"
                                : s.backlinkStatus === "missing"
                                  ? "Missing Outpost link on site"
                                  : s.backlinkStatus === "pending"
                                    ? "Pending verify"
                                    : "Add website + reciprocal link"}
                              {s.url ? (
                                <>
                                  {" · "}
                                  <button
                                    type="button"
                                    className="op-linkish"
                                    disabled={busy}
                                    onClick={() =>
                                      void verifyStartupBacklink(s.id)
                                    }
                                  >
                                    Verify
                                  </button>
                                </>
                              ) : null}
                              {s.backlinkNote ? (
                                <small className="op-backlink-note">
                                  {s.backlinkNote}
                                </small>
                              ) : null}
                            </dd>
                          </div>
                          <div>
                            <dt>Channels</dt>
                            <dd>
                              {s.channels.length
                                ? s.channels.join(" · ")
                                : "—"}
                            </dd>
                          </div>
                          {s.productId && onUploadLogo ? (
                            <div>
                              <dt>Logo</dt>
                              <dd>
                                <div className="op-profile-logo-row op-profile-logo-inline">
                                  <span
                                    className="op-product-logo"
                                    style={{ width: 40, height: 40 }}
                                    aria-hidden
                                  >
                                    {logoPreviews[s.productId] ? (
                                      <img
                                        src={logoPreviews[s.productId]}
                                        alt=""
                                      />
                                    ) : (
                                      s.name.slice(0, 2).toUpperCase()
                                    )}
                                  </span>
                                  <label className="op-profile-logo-upload">
                                    <span>
                                      {logoBusyId === s.productId
                                        ? "Uploading…"
                                        : logoPreviews[s.productId]
                                          ? "Replace"
                                          : "Upload (required)"}
                                    </span>
                                    <input
                                      type="file"
                                      accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                      disabled={logoBusyId === s.productId}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = "";
                                        if (file && s.productId)
                                          onUploadLogo(s.productId, file);
                                      }}
                                    />
                                  </label>
                                </div>
                              </dd>
                            </div>
                          ) : null}
                        </dl>
                      )}
                    </li>
                  );
                })}
              </ul>
              {myStartups.length === 0 && editTarget !== "new" ? (
                <p className="op-profile-products-lead">
                  No startups yet — create one to open a product space.
                </p>
              ) : null}
            </section>
          </div>
        </div>
      ) : activeDest === "products" ? (
        <div
          ref={productsRef}
          id="op-community-products"
          className="op-community-products"
        >
          {(() => {
            const needsLogo = productDirectory.filter(
              (s) =>
                s.productId &&
                !startupHasLogo(s) &&
                s.founderActorId === selfActorId,
            );
            const listed = productDirectory.filter(
              (s) => startupHasLogo(s) || !s.productId,
            );
            return (
              <>
                {needsLogo.length > 0 ? (
                  <div className="op-community-needs-rail">
                    <div className="op-section-title" style={{ marginTop: 0 }}>
                      <h2>Needs logo</h2>
                      <span className="op-tag op-tag-ember">
                        {needsLogo.length}
                      </span>
                    </div>
                    <div className="op-community-needs-row">
                      {needsLogo.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="op-product-card is-missing"
                          onClick={() => openProfileEditStartup(s.id)}
                        >
                          <span className="op-product-card-mark">Add logo</span>
                          <strong>{s.name}</strong>
                          <small>Required to list</small>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {listed.length === 0 && needsLogo.length === 0 ? (
                  <div className="op-list-item op-community-empty">
                    <div className="op-item-copy">
                      <h3>Directory is quiet</h3>
                      <small>
                        Use + next to Product spaces in the sidebar to register
                        one here.
                      </small>
                    </div>
                  </div>
                ) : listed.length === 0 ? (
                  <p className="op-community-waiting">
                    Waiting on a logo before anything lists here.
                  </p>
                ) : (
                  <div className="op-product-showcase" role="list">
                    {listed.map((s) => {
                      const blurb = s.tagline
                        ? trunc(s.tagline, 72)
                        : s.about
                          ? trunc(s.about, 72)
                          : null;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className="op-product-card"
                          role="listitem"
                          onClick={() => openStartup(s.id)}
                        >
                          <span className="op-product-card-logo">
                            <ProductLogo
                              token={token}
                              productId={s.productId}
                              logoR2Key={startupLogoKey(s)}
                              name={s.name}
                              previewUrl={
                                s.productId ? logoPreviews[s.productId] : null
                              }
                              large
                            />
                          </span>
                          <strong>{s.name}</strong>
                          <span className="op-product-card-stage">
                            {s.stage}
                          </span>
                          {blurb ? <p>{blurb}</p> : null}
                          <small>
                            {s.founderDisplayName || "Founder"}
                          </small>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ) : activeDest === "lookouts" ? (
        <div
          ref={lookoutsRef}
          id="op-community-lookouts"
          className="op-community-lookouts"
        >
          <div className="op-community-page-tools">
            <button
              type="button"
              className={refineOpen ? "op-chip op-chip-on" : "op-chip"}
              aria-pressed={refineOpen}
              aria-expanded={refineOpen}
              onClick={() => setRefineOpen((o) => !o)}
            >
              Filter
            </button>
          </div>

          {refineOpen ? (
            <div className="op-community-refine">
              <div
                className="op-chip-row op-channel-filters"
                role="group"
                aria-label="Lookout level"
              >
                {LEVEL_FILTERS.map((lv) => (
                  <button
                    key={String(lv)}
                    type="button"
                    className={
                      levelFilter === lv ? "op-chip op-chip-on" : "op-chip"
                    }
                    aria-pressed={levelFilter === lv}
                    onClick={() => setLevelFilter(lv)}
                  >
                    {lv === "all" ? "All levels" : `L${lv}`}
                  </button>
                ))}
              </div>
              <div
                className="op-chip-row op-channel-filters"
                role="group"
                aria-label="Channel"
              >
                {CHANNEL_FILTERS.map((ch) => (
                  <button
                    key={ch}
                    type="button"
                    className={
                      channelFilter === ch ? "op-chip op-chip-on" : "op-chip"
                    }
                    aria-pressed={channelFilter === ch}
                    onClick={() => setChannelFilter(ch)}
                  >
                    {ch === "all" ? "All" : ch}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {(() => {
            const sorted = [...directory].sort((a, b) => {
              const score = (f: CommunityActor) =>
                (f.isYou ? 4000 : 0) +
                (follows.has(f.id) ? 2000 : 0) +
                (isNewJoin(f.joinedAt) ? 1000 : 0) +
                f.lookoutLevel;
              return score(b) - score(a);
            });
            const fresh = sorted.filter((f) => isNewJoin(f.joinedAt));
            const rest = sorted.filter((f) => !isNewJoin(f.joinedAt));

            if (sorted.length === 0) {
              return (
                <div className="op-list-item op-community-empty">
                  <img
                    src="/brand/outpost16-no-quests-compass.png"
                    alt=""
                    width={44}
                    height={44}
                  />
                  <div className="op-item-copy">
                    <h3>No lookouts match</h3>
                    <small>Clear filters or invite a founder.</small>
                    <div className="op-form-row" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() => {
                          setLevelFilter("all");
                          setChannelFilter("all");
                        }}
                      >
                        Clear filters
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            const renderCard = (f: CommunityActor) => (
              <article
                key={f.id}
                className={`op-lookout-card${f.isYou ? " is-you" : ""}${follows.has(f.id) ? " is-following" : ""}`}
              >
                <button
                  type="button"
                  className="op-lookout-card-hit"
                  onClick={() => openFounder(f.id)}
                  aria-label={`Open profile for ${f.displayName}`}
                >
                  <ActorAvatar
                    name={f.displayName}
                    emoji={f.avatarEmoji}
                    imageR2Key={f.avatarR2Key}
                    token={token}
                    size="lg"
                  />
                  <strong>{f.displayName}</strong>
                  <div className="op-lookout-card-meta">
                    <span className="op-level-pill">L{f.lookoutLevel}</span>
                    {isNewJoin(f.joinedAt) ? (
                      <span className="op-community-new-pill">New</span>
                    ) : null}
                    {follows.has(f.id) && !f.isYou ? (
                      <span className="op-lookout-following-tag">
                        Following
                      </span>
                    ) : null}
                  </div>
                  <p>
                    {f.oneLiner
                      ? trunc(f.oneLiner, 88)
                      : "Lookout waiting for a one-liner"}
                  </p>
                  {f.startupName ? (
                    <span className="op-lookout-product">{f.startupName}</span>
                  ) : null}
                </button>
                {f.isYou ? (
                  <button
                    type="button"
                    className="op-btn op-lookout-card-cta"
                    onClick={() => goDest("profile")}
                  >
                    Edit profile
                  </button>
                ) : (
                  <button
                    type="button"
                    className={
                      follows.has(f.id)
                        ? "op-btn op-lookout-card-cta"
                        : "op-primary op-lookout-card-cta"
                    }
                    aria-pressed={follows.has(f.id)}
                    disabled={busy}
                    onClick={() => void toggleFollow(f.id)}
                  >
                    {follows.has(f.id) ? "Following" : "Follow"}
                  </button>
                )}
              </article>
            );

            return (
              <>
                {fresh.length > 0 ? (
                  <div className="op-community-fresh-rail">
                    <div className="op-section-title" style={{ marginTop: 0 }}>
                      <h2>Just arrived</h2>
                      <span className="op-tag">{fresh.length}</span>
                    </div>
                    <div className="op-lookout-gallery is-rail">
                      {fresh.map(renderCard)}
                    </div>
                  </div>
                ) : null}
                <div className="op-lookout-gallery">
                  {(fresh.length > 0 ? rest : sorted).map(renderCard)}
                </div>
              </>
            );
          })()}
        </div>
      ) : activeDest === "new" ? (
        <div ref={newRef} id="op-community-new" className="op-community-new-page">
          <div className="op-section-title" style={{ marginTop: 0 }}>
            <div className="op-section-title-lead">
              <h2>New</h2>
            </div>
            <button
              type="button"
              className="op-btn"
              onClick={() => goDest("lookouts")}
            >
              ← Lookouts
            </button>
          </div>
          <small className="op-community-new-hint">Joined last 14 days</small>

          <h3 className="op-community-subhead">New lookouts</h3>
          <div className="op-list">
            {newLookouts.length === 0 ? (
              <div className="op-list-item">
                <div className="op-item-copy">
                  <small>No new lookouts right now.</small>
                </div>
              </div>
            ) : (
              newLookouts.map((f) => (
                <div key={f.id} className="op-list-item op-community-row">
                  <button
                    type="button"
                    className="op-community-row-main"
                    onClick={() => openFounder(f.id)}
                  >
                    <ActorAvatar
                      name={f.displayName}
                      emoji={f.avatarEmoji}
                      imageR2Key={f.avatarR2Key}
                      token={token}
                    />
                    <div className="op-item-copy">
                      <h3>
                        {f.displayName}{" "}
                        <span className="op-community-new-pill">New</span>
                      </h3>
                      <small>{trunc(f.oneLiner || f.bio, 72)}</small>
                    </div>
                  </button>
                  {!f.isYou ? (
                    <div className="op-community-row-actions">
                      <button
                        type="button"
                        className={
                          follows.has(f.id) ? "op-btn" : "op-primary"
                        }
                        disabled={busy}
                        onClick={() => void toggleFollow(f.id)}
                      >
                        {follows.has(f.id) ? "Following" : "Follow"}
                      </button>
                      <button
                        type="button"
                        className={
                          welcomed.has(f.id) ? "op-btn" : "op-chip"
                        }
                        disabled={busy || welcomed.has(f.id)}
                        onClick={() => void sendWelcome(f.id)}
                      >
                        {welcomed.has(f.id) ? "Welcomed" : "Welcome"}
                      </button>
                    </div>
                  ) : (
                    <span className="op-community-you">That’s you</span>
                  )}
                </div>
              ))
            )}
          </div>

          <h3 className="op-community-subhead">New startups</h3>
          <div className="op-community-gallery op-community-gallery-compact">
            {newStartups.length === 0 ? (
              <div className="op-list-item">
                <div className="op-item-copy">
                  <small>No new startups right now.</small>
                </div>
              </div>
            ) : (
              newStartups.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="op-community-product-card"
                  onClick={() => openStartup(s.id)}
                >
                  <ProductLogo
                    token={token}
                    productId={s.productId}
                    logoR2Key={startupLogoKey(s)}
                    name={s.name}
                    previewUrl={
                      s.productId ? logoPreviews[s.productId] : null
                    }
                    large
                  />
                  <strong>{s.name}</strong>
                  <span className="op-community-new-pill">New</span>
                  <small>{trunc(s.tagline, 56)}</small>
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        <div ref={feedRef} id="op-community-feed" className="op-community-feed">
            <div className="op-community-composer" id="op-community-share">
              <div className="op-chip-row" role="group" aria-label="Post type">
                {POST_KINDS.map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    className={
                      postKind === k.id ? "op-chip op-chip-on" : "op-chip"
                    }
                    aria-pressed={postKind === k.id}
                    onClick={() => setPostKind(k.id)}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
              <label
                className="op-community-composer-label"
                htmlFor="op-c-compose"
              >
                Share with lookouts
              </label>
              <textarea
                id="op-c-compose"
                ref={composerRef}
                className="op-field op-community-textarea"
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                placeholder={kindPlaceholder}
                disabled={busy}
                rows={3}
              />
              <div className="op-community-composer-tools">
                <button
                  type="button"
                  className={emojiOpen ? "op-chip op-chip-on" : "op-chip"}
                  aria-pressed={emojiOpen}
                  aria-expanded={emojiOpen}
                  onClick={() => setEmojiOpen((o) => !o)}
                >
                  Emoji
                </button>
                <label className="op-chip op-community-file-chip">
                  {pendingFile ? "Change image" : "Add image"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    hidden
                    disabled={busy}
                    onChange={(e) =>
                      onPickImage(e.target.files?.[0] ?? null)
                    }
                  />
                </label>
                {pendingFile ? (
                  <button
                    type="button"
                    className="op-btn ghost"
                    onClick={() => onPickImage(null)}
                  >
                    Remove image
                  </button>
                ) : null}
                <button
                  type="button"
                  className="op-primary"
                  disabled={busy}
                  onClick={() => void submitPost()}
                >
                  Post {labelForKind(postKind).toLowerCase()}
                </button>
              </div>
              {emojiOpen ? (
                <div
                  className="op-community-emoji-row"
                  role="group"
                  aria-label="Insert emoji"
                >
                  {COMPOSER_EMOJIS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      className="op-community-emoji-btn"
                      onClick={() => insertEmoji(e)}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              ) : null}
              {pendingPreview ? (
                <div className="op-community-pending-proof">
                  <img src={pendingPreview} alt="Pending proof preview" />
                </div>
              ) : null}
            </div>

            <div className="op-community-feed-toolbar">
              <div className="op-chip-row" role="group" aria-label="Feed scope">
                <button
                  type="button"
                  className={feedMode === "all" ? "op-chip op-chip-on" : "op-chip"}
                  aria-pressed={feedMode === "all"}
                  onClick={() => setFeedMode("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={
                    feedMode === "following" ? "op-chip op-chip-on" : "op-chip"
                  }
                  aria-pressed={feedMode === "following"}
                  onClick={() => setFeedMode("following")}
                >
                  Following
                </button>
              </div>
            </div>

            <div className="op-list op-feed">
              {feedMode === "following" && follows.size === 0 ? (
                <div className="op-list-item op-community-empty">
                  <img
                    src="/brand/outpost16-empty-inbox-binoculars.png"
                    alt=""
                    width={48}
                    height={48}
                  />
                  <div className="op-item-copy">
                    <h3>Your circle is empty</h3>
                    <small>
                      Follow someone in Lookouts to narrow this feed.
                    </small>
                    <button
                      type="button"
                      className="op-btn"
                      style={{ marginTop: 8 }}
                      onClick={() => goDest("lookouts")}
                    >
                      Go to Lookouts
                    </button>
                  </div>
                </div>
              ) : posts.length === 0 ? (
                <div className="op-list-item op-community-empty">
                  <img
                    src="/brand/outpost16-no-goals-signal-flag.png"
                    alt=""
                    width={48}
                    height={48}
                  />
                  <div className="op-item-copy">
                    <h3>Waiting for the first proof</h3>
                    <small>
                      Use the field above — share a ship update when you have
                      something to show.
                    </small>
                  </div>
                </div>
              ) : (
                posts.map((p) => {
                  const open = expandedPostId === p.id;
                  const comments = commentsByPost[p.id] ?? [];
                  const threadId = `op-thread-${p.id}`;
                  const xp = xpFromMeta(p.meta);
                  const actor = founders.find((f) => f.id === p.actorId);
                  return (
                    <article key={p.id} className="op-community-card">
                      <div className="op-community-card-main">
                        <img
                          className="op-community-type-glyph"
                          src={glyphForKind(p.postKind)}
                          alt=""
                          width={36}
                          height={36}
                        />
                        <div className="op-community-card-body">
                          <div className="op-community-card-meta">
                            <ActorAvatar
                              name={p.displayName}
                              emoji={actor?.avatarEmoji}
                              imageR2Key={actor?.avatarR2Key}
                              token={token}
                            />
                            <button
                              type="button"
                              className="op-feed-who"
                              onClick={() => openFounder(p.actorId)}
                            >
                              {p.displayName}
                            </button>
                            <span className="op-level-pill">
                              <img
                                src="/brand/outpost16-quest-level-lock.png"
                                alt=""
                                width={12}
                                height={12}
                              />
                              L{p.lookoutLevel}
                            </span>
                            <span className="op-tag">
                              {labelForKind(p.postKind)}
                            </span>
                            {xp ? <span className="op-xp-pill">{xp}</span> : null}
                          </div>
                          <p className="op-community-card-text">
                            {p.body === "(visual proof)" ? null : p.body}
                          </p>
                          {p.imageR2Key ? (
                            <ProofImage
                              token={token}
                              imageR2Key={p.imageR2Key}
                              alt={`Proof from ${p.displayName}`}
                            />
                          ) : null}
                          <div className="op-community-card-foot">
                            <small>
                              {p.meta && !xp ? `${p.meta} · ` : ""}
                              {p.meta &&
                              xp &&
                              p.meta
                                .replace(/\+?\d+\s*XP/i, "")
                                .replace(/^[\s·]+|[\s·]+$/g, "")
                                ? `${p.meta
                                    .replace(/\+?\d+\s*XP/i, "")
                                    .replace(/^[\s·]+|[\s·]+$/g, "")} · `
                                : ""}
                              {relativeTime(p.createdAt)}
                            </small>
                            <button
                              type="button"
                              className="op-btn"
                              aria-expanded={open}
                              aria-controls={threadId}
                              onClick={() => void togglePost(p.id)}
                            >
                              {open ? "Hide thread" : "Thread"}
                            </button>
                          </div>
                          {open ? (
                            <div id={threadId} className="op-community-comments">
                              {comments.length === 0 ? (
                                <small>No comments yet — add the first.</small>
                              ) : (
                                comments.map((c) => (
                                  <div
                                    key={c.id}
                                    className="op-community-comment"
                                  >
                                    <strong>{c.displayName}</strong>
                                    <p>{c.body}</p>
                                    <small>
                                      {new Date(c.createdAt).toLocaleString()}
                                    </small>
                                  </div>
                                ))
                              )}
                              <div className="op-form-row">
                                <input
                                  className="op-field"
                                  value={commentDrafts[p.id] ?? ""}
                                  onChange={(e) =>
                                    setCommentDrafts((d) => ({
                                      ...d,
                                      [p.id]: e.target.value,
                                    }))
                                  }
                                  placeholder="Add a comment"
                                  disabled={busy}
                                />
                                <button
                                  type="button"
                                  className="op-primary"
                                  disabled={busy}
                                  onClick={() => void submitComment(p.id)}
                                >
                                  Comment
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
        </div>
      )}
    </section>
  );
};
